/* eslint-disable eqeqeq */
import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import { handleTabToNext, looseEq } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import {
  AccessoryHeading,
  AccSubtab,
  accList,
  APSelect,
  ASelect,
} from '../accessoryFieldKit';
import MountingSection from '../MountingSection';
import AccessoryMountingPopup from '../AccessoryMountingPopup';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — LIGHT form (accessories/light/{view/form.js 87,
 * tmpl/form.tmpl 491}, both read in full). Type wiring (§51.2):
 *  - master_light_id ← light_params + master_switch_id ← switch_params;
 *  - style_id 2 (linear) → orientation per plane (model.orientations[plane],
 *    §49.6:803-807 — orientation_id renders null when style != 2; orientation
 *    row non-clickable when style == 1);
 *  - powered_by collection (stateStorage `powered_by` — master URL);
 *  - mounting sublist non-clickable until master_light_id;
 *  - voltage row: legacy tmpl's static `selected` uses a broken if_eq…'id'
 *    literal (tmpl :110-114), but the live legacy view JS repairs the select
 *    to light_params.voltage_id post-render, so OLD DISPLAYS the saved value.
 *    React binds to the saved param to match live behavior (6e2b verifier).
 */
const config = ACCESSORY_FORM_CONFIGS.light;

const POWERED_BY_URL = '/api/v1.0/master/v1.0.0/ahu/powered_by/';

const LightForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['electrical', 'mounting', 'switch'],
  });
  const { data: poweredBy } = useGetMasterListByUrlQuery(POWERED_BY_URL);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const lightEngine = api.catalogueFields.master_light_id;
  const lightParams = (name: string) => lightEngine?.paramValue(name);
  const switchEngine = api.catalogueFields.master_switch_id;
  const planeId = api.position ? Number(api.position.get('mounting_plane_id')) : null;
  const orientationsByPlane = model.get('orientations') as
    | Record<string, Array<{ id: number; name?: string }>>
    | null;
  const orientations =
    planeId && orientationsByPlane ? orientationsByPlane[String(planeId)] : null;
  const styleId = lightParams('style_id');
  // §49.6:803-807 — orientation renders null unless linear (style 2).
  const orientationValue = styleId == 2 ? model.get('orientation_id') : null;
  const switchSelected = !!model.get('master_switch_id');
  const switchCount = switchEngine?.cmsCount;

  return (
    <>
      <AccessoryHeading api={api} target="master_light_id" visClass="vis-light" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="light" cid={cid} name="properties" sublistClass="max-height-auto">
            <APSelect api={api} target="master_light_id" name="mfg_id" label="Manufacturer" required cmsPath="manufacturer" />
            <APSelect api={api} target="master_light_id" name="style_id" label="Style" cmsPath="style" />
            <APSelect api={api} target="master_light_id" name="series_id" label="Series" cmsPath="series" labelKey="series" />
            <APSelect api={api} target="master_light_id" name="model" label="Model" required cmsPath="model" labelKey="model" valueKey="model" />
            <APSelect api={api} target="master_light_id" name="type_id" label="Bulb Type" cmsPath="type" hideNone={false} />
            <li>
              <div className="first-two-line">
                <span className="unit">lm</span>
                <input
                  readOnly
                  value={String(lightParams('brightness') ?? '')}
                  name="brightness"
                  className="non-clickable"
                  onChange={() => undefined}
                />
                <span className="label">Brightness</span>
              </div>
            </li>
          </AccSubtab>

          <AccSubtab
            api={api}
            type="light"
            cid={cid}
            name="electrical"
            inputId={`light_${cid}electrical`} // legacy id quirk (no underscore)
            label="Electrical"
          >
            {/* voltage — legacy tmpl's static `selected` uses a broken
                if_eq…'id' comparison, but the live legacy view JS
                (changeSelectValue/syncSelect) repairs the select to
                light_params.voltage_id after render, so OLD DISPLAYS the saved
                voltage (verifier capture: OLD shows 120/1/60). Bind to the
                saved param value to match that live behavior (6e2b verifier
                fix — the earlier "replicate-the-bug" read was wrong). */}
            <li>
              <div className="single-line">
                <span className="selection">
                  {(() => {
                    const cur = accList(api.cmsAccessory, 'voltage').find((o) =>
                      looseEq(o.id, lightParams('voltage_id'))
                    );
                    return cur ? <span>{String(cur.name ?? '')}</span> : null;
                  })()}
                </span>
                <select
                  name="voltage_id"
                  className="light_params_field"
                  required
                  value={
                    lightParams('voltage_id') == null
                      ? 'None'
                      : String(lightParams('voltage_id'))
                  }
                  onChange={(e) => api.commitParamsField('master_light_id', 'voltage_id', e.target.value)}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {accList(api.cmsAccessory, 'voltage').map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label">Voltage</span>
              </div>
            </li>
            <APSelect
              api={api}
              target="master_light_id"
              name="wattage"
              label="Wattage"
              unit="W"
              cmsPath="wattage"
              labelKey="wattage"
              valueKey="wattage"
              rowClass="first-two-line"
              hideNone={false}
            />
            <ASelect api={api} name="wiring_id" label="Wiring" cmsPath="wiring" hideNone={false} />
            <ASelect api={api} name="powered_by_id" label="Powered By" options={poweredBy ?? []} />
          </AccSubtab>

          <MountingSection
            api={api}
            type="light"
            cid={cid}
            variant="light"
            sublistDisabled={model.get('master_light_id') === null}
          >
            {orientations && orientations.length ? (
              <li className={styleId == 1 ? 'non-clickable' : undefined}>
                <div className="single-line">
                  <span className="selection">
                    {orientations.find((o) => o.id == orientationValue) ? (
                      <span>
                        {String(orientations.find((o) => o.id == orientationValue)?.name ?? '')}
                      </span>
                    ) : null}
                  </span>
                  <select
                    name="orientation_id"
                    className="model_field"
                    required
                    value={orientationValue != null ? String(orientationValue) : 'None'}
                    onChange={(e) => api.commitModelField('orientation_id', e.target.value)}
                  >
                    <option value="None" className="hide">
                      - -
                    </option>
                    {orientations.map((o) => (
                      <option key={String(o.id)} value={String(o.id)}>
                        {String(o.name ?? '')}
                      </option>
                    ))}
                  </select>
                  <span className="label">Orientation</span>
                </div>
              </li>
            ) : null}
          </MountingSection>

          <AccSubtab
            api={api}
            type="light"
            cid={cid}
            name="switch"
            label="Switch"
            headerChrome={
              <div className="form-heading">
                <div className="ahu-header-btns sync-result">
                  {/* legacy conditional chrome (tmpl :424-432) PLUS the
                      count-driven classes catalogue_select_field.js:1021-1027
                      writes onto the button after every count refresh —
                      `allow_reset` (count 0 or 1) is what swaps the Smartlist
                      icon for the Reset span in CSS (styles.css:2174-2177,
                      :2282-2288), and `warning` reddens a zero count. The tmpl
                      never stamps either, so porting its class list alone left
                      the switch stuck on "Smartlist". */}
                  <button
                    type="button"
                    name="switch"
                    data-name="switch"
                    className={`btn btn-sync${
                      switchSelected || !switchCount ? ' vis-switch  synced' : ''
                    }${!switchCount ? ' warning' : ''}${
                      switchCount == 1 || switchCount == 0 ? ' allow_reset' : ''
                    } clickable`}
                    onClick={() => void switchEngine?.toggleSmartList()}
                  >
                    <span className="switches_search_result search-result">
                      {`${switchCount ?? ''} / ${switchEngine?.cmsFullCount ?? ''}`}
                    </span>
                    <i className="icon-list btn-icon-sync">Smartlist</i>
                  </button>
                  <span
                    {...{ tooltip: 'Reset' }}
                    className="icon-refresh-1 ka-has-tooltip switches_reset reset-btn"
                    onClick={() => switchEngine?.deselectModel()}
                  >
                    Reset
                  </span>
                </div>
              </div>
            }
          >
            <APSelect api={api} target="master_switch_id" name="mfg_id" label="Manufacturer" cmsPath="switch.manufacturer" hideNone={false} />
            <APSelect api={api} target="master_switch_id" name="type" label="Type" cmsPath="switch.type" labelKey="type" valueKey="type" hideNone={false} />
            <APSelect api={api} target="master_switch_id" name="model" label="Model" cmsPath="switch.model" labelKey="model" valueKey="model" hideNone={false} />
          </AccSubtab>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup api={api} typeName="light" typeValue="light" />
    </>
  );
};

export default LightForm;
